import React from 'react';
import { Target, CheckCircle } from 'lucide-react';
import { useTasks } from '@/contexts/task/TaskContext';
import useGoalsState from '@/hooks/goal-planner/useGoalsState';
import ActiveGoalsContainer from '@/components/goal-planner/ActiveGoalsContainer';
import CompletedGoalsSection from '@/components/goal-planner/CompletedGoalsSection';
import GoalsSectionSkeleton from '@/components/goal-planner/GoalsSectionSkeleton';
import { DragProvider } from '@/contexts/drag/DragContext';

// Import the compact placeholder component instead of the detailed one
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

interface GoalsSidebarProps {
  onCreateGoal?: () => void;
}

// Compact empty state component for sidebar (limited space)
const EmptyActiveGoalsCompact: React.FC<{ onCreateProject?: () => void }> = ({ onCreateProject }) => {
  const navigate = useNavigate();
  
  const handleCreateProject = () => {
    if (onCreateProject) {
      onCreateProject();
    } else {
      navigate('/project-manager');
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="flex flex-col items-center justify-center px-6 py-10 min-h-[320px]"
    >
      {/* Animated illustration */}
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.5 }}
        className="relative mb-6"
      >
        {/* Animated background circles */}
        <div className="absolute inset-0 flex items-center justify-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
            className="w-20 h-20 rounded-full border border-purple-200/30 dark:border-purple-800/30"
          />
        </div>
        
        {/* Target icon with floating animation */}
        <div className="relative z-10 w-16 h-16 bg-gradient-to-br from-purple-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
          <motion.div
            animate={{ y: [0, -2, 0] }}
            transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
          >
            <Target className="w-8 h-8 text-white" />
          </motion.div>
        </div>
      </motion.div>

      {/* Content */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.5 }}
        className="text-center mb-6 space-y-3"
      >
        <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200">
          Create Your First Project
        </h3>
        <p className="text-sm text-slate-600 dark:text-slate-400 max-w-xs leading-relaxed">
          Build your entire project empire while you sip coffee.
        </p>
      </motion.div>

      {/* CTA Button */}
      <motion.button
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.6, duration: 0.5 }}
        whileHover={{ scale: 1.05, y: -1 }}
        whileTap={{ scale: 0.95 }}
        onClick={handleCreateProject}
        className="relative group overflow-hidden bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold py-3 px-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-300"
      >
        {/* Background animation */}
        <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
        
        {/* Button content */}
        <div className="relative flex items-center gap-2">
          <span>Create Your First Project</span>
          <motion.div
            animate={{ x: [0, 1, 0] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
          >
            →
          </motion.div>
        </div>
      </motion.button>

      {/* Subtle helper text */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8, duration: 0.5 }}
        className="text-xs text-slate-400 dark:text-slate-500 mt-4"
      >
        Start small, think big
      </motion.p>
    </motion.div>
  );
};

const GoalsSidebar: React.FC<GoalsSidebarProps> = ({ onCreateGoal }) => {
  const { isLoading: isTasksLoading } = useTasks();
  const {
    activeGoals,
    completedGoals,
    showCompletedGoals,
    isLoading: isGoalsLoading,
    handleGoalClick,
    handleReorder,
    handleClearCompletedGoals,
    setShowCompletedGoals
  } = useGoalsState();

  // Wait for both tasks AND goals to load before rendering
  // This prevents goals from calculating progress with empty task arrays
  if (isTasksLoading || isGoalsLoading) {
    return (
      <DragProvider>
        <div className="space-y-3 md:space-y-6">
          {/* Active Projects Skeleton */}
          <GoalsSectionSkeleton
            title="Active Projects"
            icon={<Target className="text-purple-500 dark:text-purple-400" size={18} />}
            variant="blue"
            cardCount={2}
            showExpanded={true}
            index={0}
          />
          
          {/* Completed Goals Skeleton */}
          <GoalsSectionSkeleton
            title="Completed Goals"
            icon={<CheckCircle className="text-green-500 dark:text-green-400" size={18} />}
            variant="green"
            cardCount={1}
            showExpanded={true}
            index={1}
          />
        </div>
      </DragProvider>
    );
  }

  return (
    <DragProvider>
      <div className="space-y-3 md:space-y-6">
        {/* Active Goals */}
        {activeGoals.length > 0 ? (
          <ActiveGoalsContainer
            goals={activeGoals}
            onReorder={handleReorder}
            onGoalClick={handleGoalClick}
            onCreateGoal={onCreateGoal}
          />
        ) : (
          <EmptyActiveGoalsCompact onCreateProject={onCreateGoal} />
        )}
        
        {/* Completed Goals */}
        {completedGoals.length > 0 && (
          <CompletedGoalsSection
            goals={completedGoals}
            activeGoals={activeGoals}
            showCompleted={showCompletedGoals}
            onToggle={() => setShowCompletedGoals(!showCompletedGoals)}
            onClearCompleted={handleClearCompletedGoals}
            onCardClick={handleGoalClick}
          />
        )}
      </div>
    </DragProvider>
  );
};

export default GoalsSidebar;
